import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Tabs,
  Tab,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Skeleton,
  Snackbar,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Autocomplete,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Code,
  ContentCopy,
  Delete,
  Upload,
  Description,
  Settings,
  Palette,
  Storage,
  Build,
  Translate,
  Add,
  Edit,
  PlayArrow,
  CheckCircle,
  Error as ErrorIcon,
  Science,
  Timer,
  Send,
  Warning,
  TextFields,
  VpnKey,
  InfoOutlined,
  Chat,
  Security,
  Lightbulb,
  CloudUpload,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import languageCodesData from './language_codes_iso_639_1.json';
import { confirm } from '../../common/ConfirmDialog';

// Convert language codes to array format for Autocomplete
const LANGUAGE_OPTIONS = Object.entries(languageCodesData).map(([code, name]) => ({
  code,
  name,
  label: `${name} (${code})`,
}));

/**
 * MarketplaceThumbnailUploader
 *
 * Multipart upload for the chatbot's marketplace/My-Agents card image.
 * Posts to /api/admin/chatbots/<id>/marketplace-image which pushes the
 * file to S3 + writes the CloudFront URL onto Chatbot.avatar_image.
 * DELETE on the same URL clears the field — the marketplace card then
 * falls back to the avatar-name derived image.
 */
function MarketplaceThumbnailUploader({ chatbotId, value, onChange }) {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  // ``value`` doubles as both the admin-uploaded CloudFront URL and the
  // legacy local-path placeholder (e.g. "/kaur.png") that older code
  // wrote when nothing was uploaded. Only show a preview when it's a
  // real URL; otherwise treat as empty so the Upload button surfaces.
  const isUrl = typeof value === 'string' && /^https?:\/\//i.test(value);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Max upload size is 2 MB.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `/api/admin/chatbots/${chatbotId}/marketplace-image`,
        fd,
        { withCredentials: true }
      );
      onChange(res.data?.avatar_image || '');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Upload failed.';
      alert(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    setUploading(true);
    try {
      await axios.delete(
        `/api/admin/chatbots/${chatbotId}/marketplace-image`,
        { withCredentials: true }
      );
      onChange('');
    } catch (err) {
      alert('Could not clear image.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: '#fafafa',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          flexShrink: 0,
          bgcolor: '#fff',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isUrl ? (
          <img
            src={value}
            alt="Marketplace thumbnail"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', px: 1 }}>
            Using avatar default
          </Typography>
        )}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {isUrl ? 'Custom thumbnail set' : 'No custom thumbnail — using the chatbot\'s avatar image.'}
        </Typography>
        {isUrl && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, color: 'text.disabled', wordBreak: 'break-all' }}
          >
            {value}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<CloudUpload />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          sx={{ textTransform: 'none' }}
        >
          {uploading ? 'Uploading…' : (isUrl ? 'Replace' : 'Upload')}
        </Button>
        {isUrl && (
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={handleClear}
            disabled={uploading}
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        )}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        hidden
        onChange={handleFile}
      />
    </Box>
  );
}

// Fallback list of available avatars (matches backend AVAILABLE_AVATARS)
const DEFAULT_AVAILABLE_AVATARS = [
  { id: 'kaur', name: 'Kaur', description: 'Professional virtual assistant', default_languages: [] },
  { id: 'maya', name: 'Maya', description: 'Friendly conversational assistant', default_languages: [] },
  { id: 'anjali', name: 'Anjali', description: 'Warm and helpful assistant', default_languages: [] },
  { id: 'anya', name: 'Anya', description: 'Engaging digital assistant', default_languages: [] },
  { id: 'srujana', name: 'Srujana', description: 'Approachable virtual guide', default_languages: [] },
];

export const ChatbotEdit = () => {
  const navigate = useNavigate();
  const { chatbotId } = useParams();

  const [chatbot, setChatbot] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [embedCode, setEmbedCode] = useState('');
  const [avatarImageError, setAvatarImageError] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Tools state
  const [tools, setTools] = useState([]);
  const [editingTool, setEditingTool] = useState(null);
  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [toolParameters, setToolParameters] = useState([]);

  // API Testing state
  const [testRequestBody, setTestRequestBody] = useState('{}');
  const [testResponse, setTestResponse] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testStatus, setTestStatus] = useState(null);
  const [testResponseTime, setTestResponseTime] = useState(null);
  const [testResponseWordCount, setTestResponseWordCount] = useState(null);
  const [toolDialogTab, setToolDialogTab] = useState(0);

  // Response size limit (in words)
  const MAX_RESPONSE_WORDS = 1000;

  // Avatar configs state
  const [avatarConfigs, setAvatarConfigs] = useState([]);
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [editingAvatar, setEditingAvatar] = useState(null);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [avatarMode, setAvatarMode] = useState('single');
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);

  // System prompt dialog state
  const [systemPromptDialogOpen, setSystemPromptDialogOpen] = useState(false);
  const [tempSystemPrompt, setTempSystemPrompt] = useState('');

  // Document content state
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentContentDialogOpen, setDocumentContentDialogOpen] = useState(false);
  const [tempDocumentContent, setTempDocumentContent] = useState('');

  // Reset avatar image error when avatar configs change
  useEffect(() => {
    setAvatarImageError(false);
  }, [avatarConfigs]);

  useEffect(() => {
    fetchChatbot();
    fetchDocuments();
    fetchTools();
    fetchAvatarConfigs();
    fetchAvailableAvatars();
  }, [chatbotId]);

  const fetchChatbot = async () => {
    try {
      const response = await axios.get(`/api/admin/chatbots/${chatbotId}`, { withCredentials: true });
      setChatbot(response.data);

      // Get embed code
      const embedRes = await axios.get(`/api/admin/chatbots/${chatbotId}/embed-code`, { withCredentials: true });
      setEmbedCode(embedRes.data.embed_script);
    } catch (error) {
      console.error('Failed to fetch chatbot:', error);
      setSnackbar({ open: true, message: 'Failed to load chatbot', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`/api/admin/chatbots/${chatbotId}/knowledge-base`, { withCredentials: true });
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchTools = async () => {
    try {
      const response = await axios.get(`/api/admin/chatbots/${chatbotId}/tools`, { withCredentials: true });
      setTools(response.data.tools);
    } catch (error) {
      console.error('Failed to fetch tools:', error);
    }
  };

  const fetchAvatarConfigs = async () => {
    try {
      const response = await axios.get(`api/admin/chatbots/${chatbotId}/avatars`, { withCredentials: true });
      setAvatarConfigs(response.data.avatar_configs);
    } catch (error) {
      console.error('Failed to fetch avatar configs:', error);
    }
  };

  const fetchAvailableAvatars = async () => {
    try {
      const response = await axios.get('/api/admin/avatars', { withCredentials: true });
      setAvailableAvatars(response.data.avatars);
    } catch (error) {
      console.error('Failed to fetch available avatars:', error);
    }
  };

  const handleSave = async (showNotification = true) => {
    if (!chatbot) return;

    setSaving(true);
    try {
      await axios.put(`/api/admin/chatbots/${chatbotId}`, chatbot, { withCredentials: true });
      if (showNotification) {
        setSnackbar({ open: true, message: 'Chatbot saved successfully!', severity: 'success' });
      }
    } catch (error) {
      if (showNotification) {
        setSnackbar({ open: true, message: 'Failed to save chatbot', severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = async (newValue) => {
    // Auto-save before switching tabs
    if (chatbot) {
      setSaving(true);
      try {
        await axios.put(`/api/admin/chatbots/${chatbotId}`, chatbot, { withCredentials: true });
        setSnackbar({ open: true, message: 'Changes saved', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to save changes', severity: 'error' });
      } finally {
        setSaving(false);
      }
    }
    setActiveTab(newValue);
  };

  const handleFieldChange = (field, value) => {
    if (chatbot) {
      setChatbot({ ...chatbot, [field]: value });
    }
  };

  const handleAgentPlanChange = (field, value) => {
    if (chatbot) {
      setChatbot({
        ...chatbot,
        agent_plan: { ...(chatbot.agent_plan || {}), [field]: value },
      });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`/api/admin/chatbots/${chatbotId}/knowledge-base`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSnackbar({ open: true, message: 'Document uploaded successfully!', severity: 'success' });
      fetchDocuments();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to upload document',
        severity: 'error'
      });
    }
  };

  const handleDeleteDocument = async (documentId) => {
    const ok = await confirm({
      title: 'Delete document?',
      message: 'Are you sure you want to delete this document?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await axios.delete(`/api/admin/chatbots/${chatbotId}/knowledge-base/${documentId}`, { withCredentials: true });
      setSnackbar({ open: true, message: 'Document deleted', severity: 'success' });
      fetchDocuments();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete document', severity: 'error' });
    }
  };

  const handleViewDocument = async (doc) => {
    try {
      const response = await axios.get(`/api/admin/chatbots/${chatbotId}/knowledge-base/${doc.id}`, { withCredentials: true });
      setSelectedDocument(response.data);
      setTempDocumentContent(response.data.content || '');
      setDocumentContentDialogOpen(true);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load document content', severity: 'error' });
    }
  };

  const handleSaveDocumentContent = async () => {
    if (!selectedDocument) return;

    try {
      await axios.put(`/api/admin/chatbots/${chatbotId}/knowledge-base/${selectedDocument.id}`, {
        content: tempDocumentContent
      }, { withCredentials: true });
      setSnackbar({ open: true, message: 'Document content saved!', severity: 'success' });
      setDocumentContentDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save document content', severity: 'error' });
    }
  };

  const handleCopyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setSnackbar({ open: true, message: 'Embed code copied!', severity: 'success' });
  };

  // Helper function to convert JSON schema parameters to user-friendly format
  const parametersToArray = (params) => {
    if (!params?.properties) return [];
    const required = params.required || [];
    return Object.entries(params.properties).map(([name, prop]) => ({
      name,
      type: prop.type,
      description: prop.description || '',
      required: required.includes(name),
      enum: prop.enum,
    }));
  };

  // Helper function to convert user-friendly parameters back to JSON schema
  const arrayToParameters = (arr) => {
    const properties = {};
    const required = [];

    arr.forEach((param) => {
      if (param.name.trim()) {
        properties[param.name.trim()] = {
          type: param.type,
          description: param.description,
          ...(param.enum && param.enum.length > 0 ? { enum: param.enum } : {}),
        };
        if (param.required) {
          required.push(param.name.trim());
        }
      }
    });

    return { type: 'object', properties, required };
  };

  // Tool handlers
  const handleAddTool = () => {
    setEditingTool({
      name: '',
      description: '',
      parameters: { type: 'object', properties: {}, required: [] },
      webhook_url: '',
      http_method: 'POST',
      headers: {},
      is_active: true,
    });
    setToolParameters([]);
    // Reset API test state
    setTestRequestBody('{}');
    setTestResponse('');
    setTestStatus(null);
    setTestResponseTime(null);
    setTestResponseWordCount(null);
    setToolDialogTab(0);
    setToolDialogOpen(true);
  };

  const handleEditTool = (tool) => {
    setEditingTool({ ...tool });
    setToolParameters(parametersToArray(tool.parameters));
    // Reset API test state
    setTestRequestBody('{}');
    setTestResponse('');
    setTestStatus(null);
    setTestResponseTime(null);
    setTestResponseWordCount(null);
    setToolDialogTab(0);
    setToolDialogOpen(true);
  };

  const handleSaveTool = async () => {
    if (!editingTool) return;

    // Check if test response exceeds 10000 characters
    if (testResponse && testResponse.length > 10000) {
      setSnackbar({
        open: true,
        message: 'Cannot save tool: Your API response is greater than 10,000 characters. Please reduce the response length from your API endpoint.',
        severity: 'error'
      });
      return;
    }

    // Convert user-friendly parameters to JSON schema format
    const toolToSave = {
      ...editingTool,
      parameters: arrayToParameters(toolParameters),
    };

    try {
      if (editingTool.id) {
        await axios.put(`/api/admin/chatbots/${chatbotId}/tools/${editingTool.id}`, toolToSave, { withCredentials: true });
        setSnackbar({ open: true, message: 'Tool updated!', severity: 'success' });
      } else {
        await axios.post(`/api/admin/chatbots/${chatbotId}/tools`, toolToSave, { withCredentials: true });
        setSnackbar({ open: true, message: 'Tool created!', severity: 'success' });
      }
      setToolDialogOpen(false);
      setEditingTool(null);
      setToolParameters([]);
      fetchTools();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to save tool', severity: 'error' });
    }
  };

  // Parameter management helpers
  const handleAddParameter = () => {
    setToolParameters([
      ...toolParameters,
      { name: '', type: 'string', description: '', required: false },
    ]);
  };

  const handleUpdateParameter = (index, field, value) => {
    const updated = [...toolParameters];
    updated[index] = { ...updated[index], [field]: value };
    setToolParameters(updated);
  };

  const handleRemoveParameter = (index) => {
    setToolParameters(toolParameters.filter((_, i) => i !== index));
  };

  const handleDeleteTool = async (toolId) => {
    const ok = await confirm({
      title: 'Delete tool?',
      message: 'Are you sure you want to delete this tool?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await axios.delete(`/api/admin/chatbots/${chatbotId}/tools/${toolId}`, { withCredentials: true });
      setSnackbar({ open: true, message: 'Tool deleted', severity: 'success' });
      fetchTools();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete tool', severity: 'error' });
    }
  };

  // Generate sample JSON body from parameters
  const generateSampleBody = () => {
    const sampleBody = {};
    toolParameters.forEach((param) => {
      if (param.name.trim()) {
        switch (param.type) {
          case 'string':
            sampleBody[param.name] = param.enum && param.enum.length > 0 ? param.enum[0] : '';
            break;
          case 'number':
            sampleBody[param.name] = 0;
            break;
          case 'integer':
            sampleBody[param.name] = 0;
            break;
          case 'boolean':
            sampleBody[param.name] = false;
            break;
          default:
            sampleBody[param.name] = '';
        }
      }
    });
    setTestRequestBody(JSON.stringify(sampleBody, null, 2));
  };

  // Helper function to count words in a string
  const countWords = (text) => {
    if (!text) return 0;
    // Remove extra whitespace and split by spaces
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  // Test API endpoint (via backend proxy to avoid CORS)
  const handleTestApi = async () => {
    if (!editingTool?.webhook_url) {
      setSnackbar({ open: true, message: 'Please enter a webhook URL first', severity: 'error' });
      return;
    }

    setTestLoading(true);
    setTestResponse('');
    setTestStatus(null);
    setTestResponseTime(null);
    setTestResponseWordCount(null);

    const startTime = Date.now();

    try {
      let requestBody;
      try {
        requestBody = JSON.parse(testRequestBody);
      } catch {
        setSnackbar({ open: true, message: 'Invalid JSON in request body', severity: 'error' });
        setTestLoading(false);
        return;
      }

      // Use backend proxy to avoid CORS issues
      const response = await axios.post('/api/tools/test-api', {
        url: editingTool.webhook_url,
        method: editingTool.http_method || 'POST',
        body: requestBody,
        headers: editingTool.headers || {},
      }, { withCredentials: true });

      const endTime = Date.now();
      setTestResponseTime(endTime - startTime);

      const proxyResult = response.data;
      setTestStatus({ code: proxyResult.status_code, ok: proxyResult.ok });

      // Format the response data
      let responseText;
      if (typeof proxyResult.data === 'object') {
        responseText = JSON.stringify(proxyResult.data, null, 2);
      } else {
        responseText = proxyResult.data;
      }

      setTestResponse(responseText);

      // Count words in response
      const wordCount = countWords(responseText);
      setTestResponseWordCount(wordCount);

      // Check if response exceeds 10000 characters
      if (responseText.length > 10000) {
        setSnackbar({
          open: true,
          message: 'Warning: Your tool response is greater than 10,000 characters. Please reduce the response length before saving this tool.',
          severity: 'warning'
        });
      }

    } catch (error) {
      const endTime = Date.now();
      setTestResponseTime(endTime - startTime);

      if (error.response) {
        setTestStatus({ code: error.response.status, ok: false });
        const errorText = JSON.stringify(error.response.data, null, 2);
        setTestResponse(errorText);
        setTestResponseWordCount(countWords(errorText));
      } else {
        setTestStatus({ code: 0, ok: false });
        const errorMsg = `Error: ${error.message}`;
        setTestResponse(errorMsg);
        setTestResponseWordCount(countWords(errorMsg));
      }
    } finally {
      setTestLoading(false);
    }
  };

  // Copy response to clipboard
  const handleCopyResponse = () => {
    navigator.clipboard.writeText(testResponse);
    setSnackbar({ open: true, message: 'Response copied to clipboard!', severity: 'success' });
  };

  // Get available avatars with fallback
  const getAvailableAvatarsList = () => {
    return availableAvatars && availableAvatars.length > 0 ? availableAvatars : DEFAULT_AVAILABLE_AVATARS;
  };

  // Avatar handlers
  const handleAddAvatar = () => {
    const avatarsList = getAvailableAvatarsList();
    setEditingAvatar({
      avatar_name: avatarsList[0].id,
      languages: [],
      is_default: !avatarConfigs || avatarConfigs.length === 0,
    });
    setAvatarDialogOpen(true);
  };

  const handleEditAvatar = (avatar) => {
    setEditingAvatar({ ...avatar });
    setAvatarDialogOpen(true);
  };

  const handleSaveAvatar = async () => {
    if (!editingAvatar || !editingAvatar.avatar_name) return;

    try {
      if (editingAvatar.id) {
        // Update existing avatar
        await axios.put(`/api/admin/chatbots/${chatbotId}/avatars/${editingAvatar.id}`, editingAvatar, { withCredentials: true });
        setSnackbar({ open: true, message: 'Avatar config updated!', severity: 'success' });
        fetchAvatarConfigs();
      } else {
        // For single avatar mode, if there's already an avatar, update it instead of creating new
        if (avatarMode === 'single' && avatarConfigs.length > 0) {
          const existingAvatar = avatarConfigs[0];
          await axios.put(
            `/api/admin/chatbots/${chatbotId}/avatars/${existingAvatar.id}`,
            editingAvatar,
            { withCredentials: true }
          );
          setSnackbar({ open: true, message: 'Avatar config updated!', severity: 'success' });
        } else {
          // Add new avatar
          await axios.post(`/api/admin/chatbots/${chatbotId}/avatars`, editingAvatar, { withCredentials: true });
          setSnackbar({ open: true, message: 'Avatar added!', severity: 'success' });
        }

        // Update local state for immediate UI feedback
        if (selectedSlotIndex !== null) {
          setAvatarConfigs(prev => {
            const newConfigs = [...prev];
            newConfigs[selectedSlotIndex] = editingAvatar;
            return newConfigs;
          });
        }

        // Fetch fresh data from backend
        fetchAvatarConfigs();
      }

      setAvatarDialogOpen(false);
      setEditingAvatar(null);
      setSelectedSlotIndex(null);
    } catch (error) {
      console.error('Failed to save avatar:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save avatar config';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleDeleteAvatar = async (avatarId) => {
    const ok = await confirm({
      title: 'Remove avatar?',
      message: 'Are you sure you want to remove this avatar?',
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await axios.delete(`/api/admin/chatbots/${chatbotId}/avatars/${avatarId}`, { withCredentials: true });
      setSnackbar({ open: true, message: 'Avatar removed', severity: 'success' });
      fetchAvatarConfigs();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to remove avatar', severity: 'error' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 3, borderRadius: 2 }} />
      </Box>
    );
  }

  if (!chatbot) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Chatbot not found</Typography>
        <Button onClick={() => navigate('/chatbotslist')} sx={{ mt: 2 }}>
          Back to Chatbots
        </Button>
      </Box>
    );
  }

  return (

    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/configurations/chatbotconfigurations')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {chatbot.name}
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              {chatbot.company_name || 'No company name'}
            </Typography> */}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={() => handleSave()}
          disabled={saving}
          sx={{
            bgcolor: '#014670',
            '&:hover': { bgcolor: '#014670' },
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => handleTabChange(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tooltip title="Configure basic chatbot details, AI behavior, and model settings" arrow placement="top">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="1" size="small" sx={{ bgcolor: '#014670', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
                  <Settings sx={{ fontSize: 20 }} />
                  Configuration
                </Box>
              }
            />
          </Tooltip>
          <Tooltip title="Upload documents to give your chatbot specific knowledge and context" arrow placement="top">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="2" size="small" sx={{ bgcolor: '#014670', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
                  <Storage sx={{ fontSize: 20 }} />
                  Knowledge Base
                </Box>
              }
            />
          </Tooltip>
          <Tooltip title="Create custom functions and API integrations for your chatbot" arrow placement="top">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="3" size="small" sx={{ bgcolor: '#014670', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
                  <Build sx={{ fontSize: 20 }} />
                  Custom Tools
                </Box>
              }
            />
          </Tooltip>
          <Tooltip title="Configure avatars and language support for multilingual conversations" arrow placement="top">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="4" size="small" sx={{ bgcolor: '#014670', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
                  <Translate sx={{ fontSize: 20 }} />
                  Avatars & Languages
                </Box>
              }
            />
          </Tooltip>
          <Tooltip title="Customize your chatbot widget's colors, position, and visual appearance" arrow placement="top">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="5" size="small" sx={{ bgcolor: '#014670', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
                  <Palette sx={{ fontSize: 20 }} />
                  Appearance
                </Box>
              }
            />
          </Tooltip>
          <Tooltip title="Get the embed code to add your chatbot to your website" arrow placement="top">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="6" size="small" sx={{ bgcolor: '#014670', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
                  <Code sx={{ fontSize: 20 }} />
                  Embed Code
                </Box>
              }
            />
          </Tooltip>
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <InfoOutlined sx={{ color: '#014670', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Basic Information
                  </Typography>
                  <Tooltip
                    title="Essential details about your chatbot that help identify and describe it"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Chatbot Name *
                    </Typography>
                    <Tooltip
                      title="A unique name for your chatbot. This helps you identify it in your dashboard."
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    value={chatbot.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder="e.g., Customer Support Bot, Sales Assistant"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#014670' },
                        '&.Mui-focused fieldset': { borderColor: '#014670' },
                      },
                    }}
                  />
                </Box>

                {/* <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Company Name
                    </Typography>
                    <Tooltip
                      title="Your company or organization name. This may be shown to users during conversations."
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    value={chatbot.company_name}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                    placeholder="e.g., Acme Corporation"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#014670' },
                        '&.Mui-focused fieldset': { borderColor: '#014670' },
                      },
                    }}
                  />
                </Box> */}

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Description
                    </Typography>
                    <Tooltip
                      title="A brief description of your chatbot's purpose. This is for your reference only."
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={chatbot.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Describe what this chatbot does and who it's for..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#014670' },
                        '&.Mui-focused fieldset': { borderColor: '#014670' },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* AI Configuration */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Settings sx={{ color: '#014670', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight={600}>
                    AI Configuration
                  </Typography>
                  <Tooltip
                    title="Control how your AI chatbot behaves and responds to users"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    System Prompt *
                  </Typography>
                  <Tooltip
                    title="Instructions that define your chatbot's personality, behavior, and knowledge. This is the foundation of how your AI will interact with users."
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Box
                  onClick={() => {
                    setTempSystemPrompt(chatbot.system_prompt);
                    setSystemPromptDialogOpen(true);
                  }}
                  sx={{
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: 'rgba(0,0,0,0.12)',
                    borderRadius: '8px',
                    p: 2,
                    minHeight: 200,
                    maxHeight: 200,
                    overflow: 'auto',
                    bgcolor: 'rgba(124, 173, 24, 0.03)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#014670',
                      bgcolor: 'rgba(124, 173, 24, 0.08)',
                      boxShadow: '0 2px 8px rgba(124, 173, 24, 0.15)',
                    },
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(124, 173, 24, 0.1)',
                      color: '#014670',
                      px: 1,
                      py: 0.5,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    <Edit sx={{ fontSize: 12 }} />
                    Click to edit
                  </Box>
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: chatbot.system_prompt ? 'text.primary' : 'text.secondary',
                      fontSize: '1rem',
                      pr: 10,
                    }}
                  >
                    {chatbot.system_prompt || 'Click to edit system prompt...'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Instructions that define the chatbot's behavior and personality.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Model Settings */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Settings sx={{ color: '#014670', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Model Settings
                  </Typography>
                  <Tooltip
                    title="Fine-tune how the AI model generates responses"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        Voice
                      </Typography>
                      <Tooltip
                        title="Choose the voice personality for your chatbot. Each voice has unique characteristics and tone."
                        arrow
                        placement="top"
                      >
                        <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <FormControl fullWidth>
                      <Select
                        value={chatbot.voice}
                        onChange={(e) => handleFieldChange('voice', e.target.value)}
                        sx={{
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#014670' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#014670' },
                        }}
                      >
                        <MenuItem value="shimmer">Shimmer (warm, expressive)</MenuItem>
                        <MenuItem value="alloy">Alloy (neutral, balanced)</MenuItem>
                        <MenuItem value="echo">Echo (clear, professional)</MenuItem>
                        <MenuItem value="ash">Ash (smooth, calm)</MenuItem>
                        <MenuItem value="ballad">Ballad (melodic, gentle)</MenuItem>
                        <MenuItem value="coral">Coral (bright, friendly)</MenuItem>
                        <MenuItem value="sage">Sage (wise, soothing)</MenuItem>
                        <MenuItem value="verse">Verse (articulate, poetic)</MenuItem>
                        <MenuItem value="marin">Marin (fresh, energetic)</MenuItem>
                        <MenuItem value="cedar">Cedar (deep, grounded)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        Temperature: {chatbot.temperature}
                      </Typography>
                      <Tooltip
                        title="Controls creativity: Lower (0-0.7) = more focused and consistent, Higher (0.8-2.0) = more creative and varied. Recommended: 0.7-1.0"
                        arrow
                        placement="top"
                      >
                        <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Box sx={{ px: 1, pt: 1, pb: 2 }}>
                      <Slider
                        value={chatbot.temperature}
                        onChange={(_, value) => handleFieldChange('temperature', value)}
                        min={0}
                        max={2}
                        step={0.1}
                        marks={[
                          { value: 0, label: 'Focused' },
                          { value: 0.8, label: 'Recommended' },
                          { value: 2, label: 'Creative' },
                        ]}
                        sx={{
                          color: '#014670',
                          '& .MuiSlider-thumb': {
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: '0 0 0 8px rgba(124, 173, 24, 0.16)',
                            },
                          },
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem',
                            top: 28,
                          },
                          '& .MuiSlider-markLabel[data-index="1"]': {
                            color: '#014670',
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        Max Tokens
                      </Typography>
                      <Tooltip
                        title="Maximum length of responses. Higher values allow longer responses but use more resources. Recommended: 2048-4096"
                        arrow
                        placement="top"
                      >
                        <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <TextField
                      type="number"
                      fullWidth
                      value={chatbot.max_tokens}
                      onChange={(e) => handleFieldChange('max_tokens', parseInt(e.target.value))}
                      placeholder="4096"
                      inputProps={{ min: 256, max: 16384, step: 256 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#014670' },
                          '&.Mui-focused fieldset': { borderColor: '#014670' },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing & Pricing */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Settings sx={{ color: '#014670', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Billing & Pricing
                  </Typography>
                  <Tooltip
                    title="Agent price and pack duration"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        Chatbot Price *
                      </Typography>
                      <Tooltip
                        title="Price per pack (e.g., 149.00)"
                        arrow
                        placement="top"
                      >
                        <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <TextField
                      type="number"
                      fullWidth
                      value={chatbot.agent_plan?.pack_price ?? 0}
                      onChange={(e) =>
                        handleAgentPlanChange(
                          'pack_price',
                          e.target.value === '' ? 0 : parseFloat(e.target.value)
                        )
                      }
                      placeholder="e.g., 149.00"
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#014670' },
                          '&.Mui-focused fieldset': { borderColor: '#014670' },
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        Duration ({chatbot.agent_plan?.unit_label || 'mins'}) *
                      </Typography>
                      <Tooltip
                        title="Number of units included in the pack (e.g., 50)"
                        arrow
                        placement="top"
                      >
                        <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <TextField
                      type="number"
                      fullWidth
                      value={chatbot.agent_plan?.pack_units ?? 0}
                      onChange={(e) =>
                        handleAgentPlanChange(
                          'pack_units',
                          e.target.value === '' ? 0 : parseFloat(e.target.value)
                        )
                      }
                      placeholder="e.g., 50"
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#014670' },
                          '&.Mui-focused fieldset': { borderColor: '#014670' },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Marketplace Listing — description, features, disclaimer, is_active */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Description sx={{ color: '#014670', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Marketplace Listing
                  </Typography>
                  <Tooltip
                    title="Description, features, and disclaimer shown on the customer marketplace card"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Marketplace thumbnail
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
                    Shown on the marketplace card and on each customer's My Agents listing.
                    Recommended: square PNG/JPG, ~256×256 px, transparent or white background.
                    If blank, falls back to the avatar image derived from the selected Avatar.
                  </Typography>
                  <MarketplaceThumbnailUploader
                    chatbotId={chatbotId}
                    value={chatbot.avatar_image}
                    onChange={(url) => setChatbot((prev) => ({ ...prev, avatar_image: url }))}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Marketplace description
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={chatbot.agent_plan?.marketplace_description ?? ''}
                    onChange={(e) => handleAgentPlanChange('marketplace_description', e.target.value)}
                    placeholder="Short blurb shown on the marketplace card. Falls back to the chatbot's own description when empty."
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Features <Typography component="span" variant="caption" color="text.secondary">(up to 15)</Typography>
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => {
                        const current = Array.isArray(chatbot.agent_plan?.features) ? chatbot.agent_plan.features : [];
                        if (current.length >= 15) return;
                        handleAgentPlanChange('features', [...current, '']);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Feature
                    </Button>
                  </Box>
                  {(!chatbot.agent_plan?.features || chatbot.agent_plan.features.length === 0) && (
                    <Typography variant="body2" color="text.secondary">
                      No features yet. Click "Add Feature" to start.
                    </Typography>
                  )}
                  {(chatbot.agent_plan?.features || []).map((feat, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 28, height: 28, borderRadius: '50%',
                          bgcolor: 'primary.light', color: 'primary.dark',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, fontWeight: 600, flexShrink: 0,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        value={feat ?? ''}
                        placeholder={`Feature ${idx + 1}`}
                        onChange={(e) => {
                          const updated = [...(chatbot.agent_plan?.features || [])];
                          updated[idx] = e.target.value;
                          handleAgentPlanChange('features', updated);
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const updated = [...(chatbot.agent_plan?.features || [])];
                          updated.splice(idx, 1);
                          handleAgentPlanChange('features', updated);
                        }}
                        sx={{ color: '#da3333' }}
                        title="Remove feature"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Disclaimer
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={chatbot.agent_plan?.disclaimer ?? ''}
                    onChange={(e) => handleAgentPlanChange('disclaimer', e.target.value)}
                    placeholder="Optional. Rendered in a yellow warning box at the bottom of the card."
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={chatbot.agent_plan?.is_active !== false}
                      onChange={(e) => handleAgentPlanChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active on marketplace"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Storage sx={{ color: '#014670', fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Knowledge Base Documents
                </Typography>
                <Tooltip
                  title="Upload documents to give your chatbot specific knowledge and context. The AI will reference these documents when answering questions."
                  arrow
                  placement="top"
                >
                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Button
                variant="contained"
                startIcon={<Upload />}
                component="label"
                sx={{
                  bgcolor: '#014670',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#014670',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(124, 173, 24, 0.3)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Upload Document
                <input
                  type="file"
                  hidden
                  accept=".pdf,.txt,.docx,.doc,.md"
                  onChange={handleFileUpload}
                />
              </Button>
            </Box>

            <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3, fontSize: '0.85rem' }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                Supported file formats:
              </Typography>
              <Typography variant="caption">
                PDF, TXT, DOCX, DOC, MD • Maximum file size: 10MB • The AI will use this content to provide accurate, context-aware responses
              </Typography>
            </Alert>

            {documents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Description sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">No documents uploaded yet</Typography>
              </Box>
            ) : (
              <List>
                {documents.map((doc) => (
                  <ListItem
                    key={doc.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ListItemText
                        primary={doc.name}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption">{formatFileSize(doc.file_size)}</Typography>
                            <Typography variant="caption">{doc.file_type.toUpperCase()}</Typography>
                            <Chip
                              label={doc.status}
                              size="small"
                              color={doc.status === 'completed' ? 'success' : doc.status === 'processing' ? 'warning' : 'default'}
                            />
                            {doc.chunk_count > 0 && (
                              <Typography variant="caption">{doc.chunk_count} chunks</Typography>
                            )}
                          </Box>
                        }
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Description sx={{ fontSize: 16 }} />}
                        onClick={() => handleViewDocument(doc)}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: '#014670',
                          color: '#014670',
                          '&:hover': {
                            borderColor: '#014670',
                            bgcolor: 'rgba(124, 173, 24, 0.08)',
                          },
                        }}
                      >
                        View & Edit Content
                      </Button>
                      <IconButton
                        onClick={() => handleDeleteDocument(doc.id)}
                        title="Delete"
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            bgcolor: 'error.50',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Custom Tools */}
      {activeTab === 2 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6">Custom Tools / Function Calling</Typography>
                <Typography variant="body2" color="text.secondary">
                  Define custom functions that the AI can call during conversations
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddTool}
                sx={{ bgcolor: '#014670', '&:hover': { bgcolor: '#014670' } }}
              >
                Add Tool
              </Button>
            </Box>

            {tools.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Build sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">No custom tools configured</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Add tools to enable function calling capabilities
                </Typography>
              </Box>
            ) : (
              <List>
                {tools.map((tool) => (
                  <ListItem
                    key={tool.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight={600}>{tool.name}</Typography>
                          <Chip label="function" size="small" color="primary" variant="outlined" />
                        </Box>
                      }
                      secondary={tool.description}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleEditTool(tool)} sx={{ mr: 1 }}>
                        <Edit />
                      </IconButton>
                      <IconButton edge="end" onClick={() => tool.id && handleDeleteTool(tool.id)}>
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 3: Avatars & Languages */}
      {activeTab === 3 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6">Avatar & Language Configuration</Typography>
                <Tooltip
                  title="Configure different avatars for different languages. Each avatar can handle specific languages, enabling multilingual support with appropriate visual representation."
                  arrow
                  placement="top"
                >
                  <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Choose between single or multiple avatars for multilingual support
              </Typography>
            </Box>

            {/* Toggle: Single/Multiple Avatars */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Avatar Mode
                </Typography>
                <Tooltip
                  title="Single mode: Use one avatar for all languages. Multiple mode: Use up to 3 different avatars for different languages."
                  arrow
                  placement="top"
                >
                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <ToggleButtonGroup
                value={avatarMode}
                exclusive
                onChange={async (_, newMode) => {
                  if (newMode === null || newMode === avatarMode) return;
                  // If there are configs to clear, confirm BEFORE flipping
                  // the mode. Original code flipped first then asked, which
                  // left an inconsistent state on Cancel.
                  if (avatarConfigs.length > 0) {
                    const ok = await confirm({
                      title: 'Switch avatar mode?',
                      message: 'Switching modes will reset your avatar configurations. Do you want to continue?',
                      confirmLabel: 'Switch',
                      variant: 'warning',
                    });
                    if (!ok) return;
                    setAvatarConfigs([]);
                  }
                  setAvatarMode(newMode);
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    border: '2px solid',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      bgcolor: '#014670',
                      color: 'white',
                      borderColor: '#014670',
                      '&:hover': {
                        bgcolor: '#014670',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="single">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 20 }} />
                    Single Avatar
                  </Box>
                </ToggleButton>
                <ToggleButton value="multiple">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Translate sx={{ fontSize: 20 }} />
                    Multiple Avatars (up to 3)
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Avatar Slots */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {avatarMode === 'single' ? 'Your Avatar' : `Your Avatars (${avatarConfigs.length}/${3})`}
                </Typography>
                <Tooltip
                  title={
                    avatarMode === 'single'
                      ? "Click on the slot to select an avatar for all languages"
                      : "Click on any empty slot to add an avatar with specific language configuration"
                  }
                  arrow
                >
                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>

              <Grid container spacing={3}>
                {Array.from({ length: avatarMode === 'single' ? 1 : 3 }).map((_, index) => {
                  const config = avatarConfigs[index];
                  const isConfigured = !!config;

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: avatarMode === 'single' ? 12 : 4 }} key={index}>
                      <Paper
                        onClick={() => {
                          setSelectedSlotIndex(index);
                          if (isConfigured) {
                            // Edit existing configuration
                            setEditingAvatar(config);
                          } else {
                            // Create new configuration
                            setEditingAvatar({
                              avatar_name: '',
                              languages: [],
                              is_default: index === 0,
                            });
                          }
                          setAvatarDialogOpen(true);
                        }}
                        elevation={0}
                        sx={{
                          border: '2px dashed',
                          borderColor: isConfigured ? '#014670' : 'rgba(0,0,0,0.12)',
                          borderRadius: 3,
                          p: 3,
                          cursor: 'pointer',
                          bgcolor: isConfigured ? 'rgba(124, 173, 24, 0.03)' : 'transparent',
                          transition: 'all 0.3s ease',
                          minHeight: avatarMode === 'single' ? 200 : 280,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover': {
                            borderColor: '#014670',
                            borderStyle: 'solid',
                            boxShadow: '0 4px 12px rgba(124, 173, 24, 0.15)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {!isConfigured ? (
                          // Empty Skeleton
                          <Box sx={{ textAlign: 'center', width: '100%' }}>
                            <Skeleton
                              variant="circular"
                              width={avatarMode === 'single' ? 120 : 100}
                              height={avatarMode === 'single' ? 120 : 100}
                              sx={{
                                mx: 'auto',
                                mb: 2,
                                bgcolor: 'rgba(124, 173, 24, 0.1)',
                              }}
                            />
                            <Add sx={{ fontSize: 40, color: '#014670', opacity: 0.6, mb: 1 }} />
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Click to add avatar
                            </Typography>
                            {index === 0 && (
                              <Chip
                                label="Default"
                                size="small"
                                sx={{
                                  mt: 1,
                                  bgcolor: 'rgba(124, 173, 24, 0.1)',
                                  color: '#014670',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          // Configured Avatar
                          <Box sx={{ textAlign: 'center', width: '100%', position: 'relative' }}>
                            <Box
                              component="img"
                              src={`${process.env.REACT_APP_API_BASE_URL}api/avatars/thumbnail/${config.avatar_name}`}
                              alt={config.avatar_display_name || config.avatar_name}
                              sx={{
                                width: avatarMode === 'single' ? 120 : 100,
                                height: avatarMode === 'single' ? 120 : 100,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                mx: 'auto',
                                mb: 2,
                                border: '4px solid #014670',
                                boxShadow: '0 4px 12px rgba(124, 173, 24, 0.3)',
                              }}
                            />
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                              {config.avatar_display_name || config.avatar_name}
                            </Typography>
                            {config.is_default && (
                              <Chip
                                label="Default"
                                size="small"
                                sx={{
                                  mb: 1,
                                  bgcolor: '#014670',
                                  color: 'white',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mt: 1 }}>
                              {config.languages.length > 0 ? (
                                config.languages.map((lang, idx) => (
                                  <Chip
                                    key={idx}
                                    label={lang}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor: '#014670',
                                      color: '#014670',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                ))
                              ) : (
                                <Chip
                                  label="All languages"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: '#014670',
                                    color: '#014670',
                                    fontSize: '0.7rem',
                                  }}
                                />
                              )}
                            </Box>
                            {/* Delete button */}
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                if (config.id) {
                                  handleDeleteAvatar(config.id);
                                } else {
                                  // Remove from local state if not saved yet
                                  setAvatarConfigs(prev => prev.filter((_, i) => i !== index));
                                }
                              }}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: 'white',
                                border: '2px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                  bgcolor: 'error.light',
                                  borderColor: 'error.main',
                                  color: 'error.dark',
                                },
                              }}
                              size="small"
                            >
                              <Delete sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tab 4: Appearance */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Palette sx={{ color: '#014670', fontSize: 24 }} />
                  <Typography variant="h6">Widget Appearance</Typography>
                  <Tooltip
                    title="Customize how your chatbot widget looks on your website"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                {/* Primary Color */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Primary Color
                    </Typography>
                    <Tooltip
                      title="This color will be used for the chat button, header, and other UI elements in the widget"
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: '#014670',
                        },
                      }}
                    >
                      <input
                        type="color"
                        value={chatbot.primary_color}
                        onChange={(e) => handleFieldChange('primary_color', e.target.value)}
                        style={{
                          width: 80,
                          height: 50,
                          border: 'none',
                          cursor: 'pointer',
                          display: 'block',
                        }}
                      />
                    </Box>
                    <TextField
                      value={chatbot.primary_color}
                      onChange={(e) => handleFieldChange('primary_color', e.target.value)}
                      size="small"
                      placeholder="#014670"
                      helperText="Hex color code"
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  {/* Color presets */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: 'center' }}>
                      Presets:
                    </Typography>
                    {['#014670', '#2563eb', '#dc2626', '#7c3aed', '#059669', '#ea580c'].map((color) => (
                      <Tooltip key={color} title={color} arrow>
                        <Box
                          onClick={() => handleFieldChange('primary_color', color)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: color,
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: chatbot.primary_color === color ? '#014670' : 'divider',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              boxShadow: 2,
                            },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>

                {/* Button Position */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Button Position
                    </Typography>
                    <Tooltip
                      title="Choose where the chat button appears on your website"
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <FormControl fullWidth>
                    <Select
                      value={chatbot.button_position}
                      onChange={(e) => handleFieldChange('button_position', e.target.value)}
                      size="small"
                    >
                      <MenuItem value="bottom-right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, bgcolor: '#014670', borderRadius: '50%' }} />
                          Bottom Right (Recommended)
                        </Box>
                      </MenuItem>
                      <MenuItem value="bottom-left">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, bgcolor: '#014670', borderRadius: '50%' }} />
                          Bottom Left
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Widget Title */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Widget Title (Optional)
                    </Typography>
                    <Tooltip
                      title="A friendly greeting or message that appears above the chat button to encourage users to start a conversation"
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    value={chatbot.widget_title || ''}
                    onChange={(e) => handleFieldChange('widget_title', e.target.value)}
                    placeholder="e.g., Click to chat, Hello there!, Need help?"
                    size="small"
                    helperText="Leave empty to hide the title"
                    inputProps={{ maxLength: 50 }}
                  />

                  {/* Widget Title Colors - Show only if title is not empty */}
                  {chatbot.widget_title && chatbot.widget_title.trim() !== '' && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                      {/* Background Color */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Typography variant="caption" fontWeight={500}>
                            Title Background
                          </Typography>
                          <Tooltip
                            title="Background color of the title bubble"
                            arrow
                            placement="top"
                          >
                            <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            type="color"
                            value={chatbot.widget_title_bg_color || '#ffffff'}
                            onChange={(e) => handleFieldChange('widget_title_bg_color', e.target.value)}
                            size="small"
                            sx={{
                              '& input[type="color"]': {
                                height: 36,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                '&:hover': {
                                  borderColor: '#014670',
                                },
                              },
                            }}
                          />
                          <TextField
                            value={chatbot.widget_title_bg_color || '#ffffff'}
                            onChange={(e) => handleFieldChange('widget_title_bg_color', e.target.value)}
                            size="small"
                            placeholder="#ffffff"
                            sx={{ width: 100 }}
                            inputProps={{ maxLength: 7 }}
                          />
                        </Box>
                      </Box>

                      {/* Text Color */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Typography variant="caption" fontWeight={500}>
                            Title Text Color
                          </Typography>
                          <Tooltip
                            title="Text color of the title"
                            arrow
                            placement="top"
                          >
                            <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            type="color"
                            value={chatbot.widget_title_text_color || '#1a1a1a'}
                            onChange={(e) => handleFieldChange('widget_title_text_color', e.target.value)}
                            size="small"
                            sx={{
                              '& input[type="color"]': {
                                height: 36,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                '&:hover': {
                                  borderColor: '#014670',
                                },
                              },
                            }}
                          />
                          <TextField
                            value={chatbot.widget_title_text_color || '#1a1a1a'}
                            onChange={(e) => handleFieldChange('widget_title_text_color', e.target.value)}
                            size="small"
                            placeholder="#1a1a1a"
                            sx={{ width: 100 }}
                            inputProps={{ maxLength: 7 }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Display Title */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Connect Button Title
                    </Typography>
                    <Tooltip
                      title="The text displayed on the connect button when users can start a conversation with the avatar"
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    value={chatbot.display_title || ''}
                    onChange={(e) => handleFieldChange('display_title', e.target.value)}
                    placeholder="e.g., Speak to Bharathi, Start Conversation, Chat Now"
                    size="small"
                    helperText="This appears on the call button in your avatar video"
                    inputProps={{ maxLength: 100 }}
                  />
                </Box>

                {/* Widget Avatar Info */}
                <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Widget Avatar
                    </Typography>
                    <Tooltip
                      title="The chat widget will display your default avatar. Configure avatars in the 'Avatars & Languages' section."
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="caption">
                    The widget will use your default avatar from the Avatars & Languages section.
                    {avatarConfigs.find(a => a.is_default) ? (
                      <> Currently using: <strong>{avatarConfigs.find(a => a.is_default)?.avatar_display_name || avatarConfigs.find(a => a.is_default)?.avatar_name}</strong></>
                    ) : (
                      <> No default avatar configured yet.</>
                    )}
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Typography variant="h6">Live Preview</Typography>
                  <Tooltip
                    title="See how your widget will look on your website"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                {/* Preview Container */}
                <Box
                  sx={{
                    position: 'relative',
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'divider',
                    p: 3,
                    minHeight: 300,
                    backgroundImage: 'linear-gradient(45deg, rgba(0,0,0,0.02) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.02) 75%, rgba(0,0,0,0.02)), linear-gradient(45deg, rgba(0,0,0,0.02) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.02) 75%, rgba(0,0,0,0.02))',
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 10px 10px',
                  }}
                >
                  {/* Website mockup text */}
                  <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width="60%" height={30} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="70%" height={20} />
                  </Box>

                  {/* Chat button preview */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 20,
                      ...(chatbot.button_position === 'bottom-right' ? { right: 20 } : { left: 20 }),
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: chatbot.button_position === 'bottom-right' ? 'flex-end' : 'flex-start',
                      gap: 1,
                    }}
                  >
                    {/* Widget title bubble */}
                    {chatbot.widget_title && chatbot.widget_title.trim() !== '' && (
                      <Box
                        sx={{
                          bgcolor: chatbot.widget_title_bg_color || '#ffffff',
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          maxWidth: 200,
                          animation: 'fadeInUp 0.3s ease-out',
                          '@keyframes fadeInUp': {
                            from: {
                              opacity: 0,
                              transform: 'translateY(10px)',
                            },
                            to: {
                              opacity: 1,
                              transform: 'translateY(0)',
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: chatbot.widget_title_text_color || '#1a1a1a',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            textAlign: 'center',
                          }}
                        >
                          {chatbot.widget_title}
                        </Typography>
                      </Box>
                    )}

                    {/* Chat button */}
                    <Tooltip title="Chat Widget Button" arrow>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: chatbot.primary_color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          boxShadow: `0 4px 20px ${chatbot.primary_color}66`,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: `0 6px 24px ${chatbot.primary_color}99`,
                          },
                        }}
                      >
                        {(() => {
                          const defaultAvatar = avatarConfigs.find(a => a.is_default);
                          return defaultAvatar && !avatarImageError ? (
                            <Box
                              component="img"
                              src={`${process.env.REACT_APP_API_BASE_URL}api/avatars/thumbnail/${defaultAvatar.avatar_name}`}
                              alt={defaultAvatar.avatar_display_name || defaultAvatar.avatar_name}
                              sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                objectFit: 'cover',
                              }}
                              onError={() => {
                                // Fallback to chat icon if image fails to load
                                setAvatarImageError(true);
                              }}
                            />
                          ) : (
                            <Chat sx={{ fontSize: 36 }} />
                          );
                        })()}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Preview info */}
                <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    The chat button will appear in the <strong>{chatbot.button_position === 'bottom-right' ? 'bottom right' : 'bottom left'}</strong> corner of your website
                  </Typography>
                  {avatarConfigs.find(a => a.is_default) ? (
                    <Typography variant="caption">
                      Displaying your default avatar: <strong>{avatarConfigs.find(a => a.is_default)?.avatar_display_name || avatarConfigs.find(a => a.is_default)?.avatar_name}</strong>
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="warning.main">
                      No default avatar configured. Using default chat icon.
                    </Typography>
                  )}
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 5: Embed Code */}
      {activeTab === 5 && (
        <Grid container spacing={3}>
          {/* Embed Code Card */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Code sx={{ color: '#014670', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Embed Code
                  </Typography>
                  <Tooltip
                    title="Copy this code and paste it into your website's HTML to add the chatbot widget"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                <Alert severity="info" sx={{ mb: 3, fontSize: '0.85rem' }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    How to add the chatbot to your website:
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                    1. Copy the embed code below
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                    2. Paste it into your website's HTML, right before the closing <code>&lt;/body&gt;</code> tag
                  </Typography>
                  <Typography variant="caption" component="div">
                    3. Save and publish your website - the chatbot will appear automatically!
                  </Typography>
                </Alert>

                {/* Embed Code Box */}
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: '#1a1a1a',
                    p: 3,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'divider',
                    mb: 2,
                    position: 'relative',
                    overflow: 'auto',
                    maxHeight: 300,
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '0.85rem',
                      color: '#fff',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                    }}
                  >
                    {embedCode}
                  </Typography>
                </Paper>

                {/* Copy Button */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ContentCopy />}
                    onClick={handleCopyEmbedCode}
                    sx={{
                      bgcolor: '#014670',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      '&:hover': {
                        bgcolor: '#014670',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(124, 173, 24, 0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Copy Embed Code
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Click to copy the code to your clipboard
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Domain Restrictions Card */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Security sx={{ color: '#014670', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Domain Restrictions (Optional)
                  </Typography>
                  <Tooltip
                    title="Control which websites can use your chatbot for added security"
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  For added security, you can restrict which domains are allowed to embed this chatbot.
                  Leave empty to allow the chatbot on any website.
                </Typography>

                <TextField
                  label="Allowed Domains"
                  fullWidth
                  placeholder="example.com, app.example.com, subdomain.example.com"
                  value={chatbot.allowed_domains.join(', ')}
                  onChange={(e) => handleFieldChange('allowed_domains', e.target.value.split(',').map(d => d.trim()).filter(Boolean))}
                  helperText="Enter comma-separated domain names (without http:// or https://)"
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#014670',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#014670',
                      },
                    },
                  }}
                />

                {chatbot.allowed_domains.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
                      Currently allowed domains:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {chatbot.allowed_domains.map((domain, index) => (
                        <Chip
                          key={index}
                          label={domain}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(124, 173, 24, 0.1)',
                            color: '#014670',
                            fontWeight: 500,
                            border: '1px solid rgba(124, 173, 24, 0.3)',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Implementation Tips Card */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'rgba(124, 173, 24, 0.02)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Lightbulb sx={{ color: '#014670', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Implementation Tips
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20, mt: 0.3 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          HTML Websites
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Paste the code directly before the <code>&lt;/body&gt;</code> tag in your HTML file
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20, mt: 0.3 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          WordPress
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Use a plugin like "Insert Headers and Footers" or add to your theme's footer.php
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20, mt: 0.3 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          React / Next.js
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Add to your _document.js or use a Script component in your layout
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20, mt: 0.3 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Shopify / Wix / Squarespace
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Use the "Custom Code" or "Code Injection" feature in your platform's settings
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Alert severity="warning" sx={{ mt: 3, fontSize: '0.85rem' }}>
                  <Typography variant="caption" fontWeight={500}>
                    Important: Make sure your chatbot status is set to "Active" for the widget to appear on your website.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={12} sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}>
          {activeTab !== 0 ? <Button
            onClick={() => handleTabChange(activeTab - 1)}
            variant="outlined"
            color='warning'
          >
            Previous
          </Button> : ""}

          {activeTab !== 5 ? <Button
            onClick={() => handleTabChange(activeTab + 1)}
            variant="contained"
            color="primary"
            sx={{
              bgcolor: '#014670',
              '&:hover': { bgcolor: '#014670' },
            }}
          >
            Next
          </Button> : ""}
        </Grid>
      </Grid>
      {/* Tool Dialog - Redesigned */}
      <Dialog
        open={toolDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return; // block close
          }
          setToolDialogOpen(false);
        }}

        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh', maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ pb: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Build sx={{ color: '#014670' }} />
            <Typography variant="h6">
              {editingTool?.id ? 'Edit Tool' : 'Add New Tool'}
            </Typography>
          </Box>
          <Tabs
            value={toolDialogTab}
            onChange={(_, v) => setToolDialogTab(v)}
            sx={{ mt: 2 }}
          >
            <Tab icon={<Settings />} iconPosition="start" label="Configuration" />
            <Tab icon={<Science />} iconPosition="start" label="Test API" />
          </Tabs>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Tab 0: Configuration */}
          {toolDialogTab === 0 && (
            <Box sx={{ p: 4 }}>
              <Grid container spacing={4}>
                {/* Left Column - Basic Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Code fontSize="small" sx={{ color: '#014670' }} />
                        Function Details
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Function Name *
                          </Typography>
                          <Tooltip
                            title="The unique identifier for this function. Use snake_case (e.g., get_weather, search_database). The AI will use this name to call your function."
                            arrow
                            placement="top"
                          >
                            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <TextField
                          fullWidth
                          value={editingTool?.name || ''}
                          onChange={(e) => setEditingTool(prev => prev ? { ...prev, name: e.target.value } : null)}
                          placeholder="e.g., get_weather, search_products"
                          helperText="Use snake_case naming convention"
                          size="small"
                        />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Description *
                          </Typography>
                          <Tooltip
                            title="Explain what this function does and when the AI should call it. Be specific about the purpose and use cases so the AI knows when to invoke this function during conversations."
                            arrow
                            placement="top"
                          >
                            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={editingTool?.description || ''}
                          onChange={(e) => setEditingTool(prev => prev ? { ...prev, description: e.target.value } : null)}
                          placeholder="Describe what this function does and when the AI should use it"
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Right Column - API Endpoint */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Send fontSize="small" sx={{ color: '#014670' }} />
                        API Endpoint
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Method
                            </Typography>
                            <Tooltip
                              title="The HTTP method to use when calling your API endpoint (GET, POST, PUT, DELETE, or PATCH)"
                              arrow
                              placement="top"
                            >
                              <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={editingTool?.http_method || 'POST'}
                              onChange={(e) => setEditingTool(prev => prev ? { ...prev, http_method: e.target.value } : null)}
                            >
                              <MenuItem value="GET">GET</MenuItem>
                              <MenuItem value="POST">POST</MenuItem>
                              <MenuItem value="PUT">PUT</MenuItem>
                              <MenuItem value="DELETE">DELETE</MenuItem>
                              <MenuItem value="PATCH">PATCH</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Webhook URL *
                            </Typography>
                            <Tooltip
                              title="The full URL of your API endpoint that will be called when the AI executes this function. Must be a valid HTTPS URL."
                              arrow
                              placement="top"
                            >
                              <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                          <TextField
                            fullWidth
                            size="small"
                            value={editingTool?.webhook_url || ''}
                            onChange={(e) => setEditingTool(prev => prev ? { ...prev, webhook_url: e.target.value } : null)}
                            placeholder="https://your-api.com/webhook/endpoint"
                          />
                        </Box>
                      </Box>
                      <Alert severity="warning" sx={{ fontSize: '0.8rem', mb: 1 }}>
                        This URL will be called when the AI decides to use this function during a conversation.
                      </Alert>
                      <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                        <strong>CORS Configuration:</strong> If your API has CORS restrictions, make sure to allow requests from this platform's domain. Add appropriate CORS headers to your API endpoint or configure your server to accept cross-origin requests.
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Full Width - HTTP Headers */}
                <Grid size={{ xs: 12 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VpnKey fontSize="small" sx={{ color: '#014670' }} />
                            HTTP Headers
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                              (Optional)
                            </Typography>
                          </Typography>
                          <Tooltip
                            title="Add custom HTTP headers like Authorization tokens, API keys, or Content-Type. These headers will be sent with every request to your webhook URL."
                            arrow
                            placement="top"
                          >
                            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => {
                            const currentHeaders = editingTool?.headers || {};
                            setEditingTool(prev => prev ? {
                              ...prev,
                              headers: { ...currentHeaders, '': '' }
                            } : null);
                          }}
                          variant="outlined"
                          sx={{ borderColor: '#014670', color: '#014670' }}
                        >
                          Add Header
                        </Button>
                      </Box>

                      {(!editingTool?.headers || Object.keys(editingTool.headers).length === 0) ? (
                        <Box sx={{ textAlign: 'center', py: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <VpnKey sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                          <Typography color="text.secondary" variant="body2">
                            No custom headers configured
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Add headers for authentication (Bearer tokens, API keys) or custom requirements
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {Object.entries(editingTool?.headers || {}).map(([key, value], index) => (
                            <Paper
                              key={index}
                              sx={{
                                p: 1.5,
                                bgcolor: 'grey.50',
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'center',
                              }}
                            >
                              <TextField
                                label="Header Name"
                                size="small"
                                value={key}
                                onChange={(e) => {
                                  const headers = { ...(editingTool?.headers || {}) };
                                  const oldValue = headers[key];
                                  delete headers[key];
                                  headers[e.target.value] = oldValue;
                                  setEditingTool(prev => prev ? { ...prev, headers } : null);
                                }}
                                placeholder="Authorization"
                                sx={{ width: 200 }}
                              />
                              <TextField
                                label="Header Value"
                                size="small"
                                value={value}
                                onChange={(e) => {
                                  const headers = { ...(editingTool?.headers || {}) };
                                  headers[key] = e.target.value;
                                  setEditingTool(prev => prev ? { ...prev, headers } : null);
                                }}
                                placeholder="Bearer your-token-here"
                                type={key.toLowerCase().includes('auth') || key.toLowerCase().includes('key') || key.toLowerCase().includes('token') ? 'password' : 'text'}
                                sx={{ flex: 1 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const headers = { ...(editingTool?.headers || {}) };
                                  delete headers[key];
                                  setEditingTool(prev => prev ? { ...prev, headers } : null);
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Paper>
                          ))}
                        </Box>
                      )}

                      <Alert severity="warning" sx={{ mt: 2, fontSize: '0.8rem' }}>
                        Headers containing sensitive data (API keys, tokens) are stored securely and never exposed to the client.
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Full Width - Parameters */}
                <Grid size={{ xs: 12 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Settings fontSize="small" sx={{ color: '#014670' }} />
                            Function Parameters
                          </Typography>
                          <Tooltip
                            title="Define the input parameters your function accepts. The AI will automatically extract these values from the conversation and pass them to your webhook. Examples: location for weather, product_id for searches."
                            arrow
                            placement="top"
                          >
                            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={handleAddParameter}
                          variant="outlined"
                          sx={{ borderColor: '#014670', color: '#014670' }}
                        >
                          Add Parameter
                        </Button>
                      </Box>

                      {toolParameters.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Settings sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography color="text.secondary">
                            No parameters defined yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Click "Add Parameter" to define what inputs this function accepts
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {toolParameters.map((param, index) => (
                            <Paper
                              key={index}
                              sx={{
                                p: 1.5,
                                bgcolor: 'grey.50',
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'center',
                              }}
                            >
                              <Tooltip title="Parameter name in snake_case (e.g., user_id, location)" arrow>
                                <TextField
                                  label="Name"
                                  size="small"
                                  value={param.name}
                                  onChange={(e) => handleUpdateParameter(index, 'name', e.target.value)}
                                  placeholder="param_name"
                                  sx={{ width: 150 }}
                                />
                              </Tooltip>
                              <Tooltip title="Data type: string (text), number (decimal), integer (whole number), or boolean (true/false)" arrow>
                                <FormControl size="small" sx={{ width: 120 }}>
                                  <InputLabel>Type</InputLabel>
                                  <Select
                                    value={param.type}
                                    label="Type"
                                    onChange={(e) => handleUpdateParameter(index, 'type', e.target.value)}
                                  >
                                    <MenuItem value="string">String</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="integer">Integer</MenuItem>
                                    <MenuItem value="boolean">Boolean</MenuItem>
                                  </Select>
                                </FormControl>
                              </Tooltip>
                              <Tooltip title="Explain what this parameter represents and how the AI should use it" arrow>
                                <TextField
                                  label="Description"
                                  size="small"
                                  value={param.description}
                                  onChange={(e) => handleUpdateParameter(index, 'description', e.target.value)}
                                  placeholder="What is this parameter for?"
                                  sx={{ flex: 1 }}
                                />
                              </Tooltip>
                              <Tooltip title="Mark as required if the function cannot work without this parameter" arrow>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={param.required}
                                      onChange={(e) => handleUpdateParameter(index, 'required', e.target.checked)}
                                      size="small"
                                      sx={{ '&.Mui-checked': { color: '#014670' } }}
                                    />
                                  }
                                  label={<Typography variant="body2">Required</Typography>}
                                  sx={{ mx: 0, minWidth: 90 }}
                                />
                              </Tooltip>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveParameter(index)}
                                sx={{ color: 'error.main' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 1: Test API */}
          {toolDialogTab === 1 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500 }}>
              {/* URL Bar */}
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'grey.50',
                  flexShrink: 0,
                }}
              >
                <Chip
                  label={editingTool?.http_method || 'POST'}
                  size="small"
                  color={
                    editingTool?.http_method === 'GET' ? 'success' :
                      editingTool?.http_method === 'POST' ? 'primary' :
                        editingTool?.http_method === 'PUT' ? 'warning' :
                          editingTool?.http_method === 'DELETE' ? 'error' : 'default'
                  }
                  sx={{ fontWeight: 600, minWidth: 70 }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    color: editingTool?.webhook_url ? 'text.primary' : 'text.disabled',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {editingTool?.webhook_url || 'No URL configured - go to Configuration tab'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={testLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
                  onClick={handleTestApi}
                  disabled={testLoading || !editingTool?.webhook_url}
                  sx={{ bgcolor: '#014670', '&:hover': { bgcolor: '#014670' }, minWidth: 100 }}
                >
                  {testLoading ? 'Sending...' : 'Send'}
                </Button>
              </Paper>

              <Grid container spacing={2} sx={{ flex: 1, minHeight: 400 }}>
                {/* Request */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                  <Card variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 350 }}>
                    <Box sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: 'grey.50',
                      flexShrink: 0,
                    }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Request Body
                      </Typography>
                      <Button
                        size="small"
                        onClick={generateSampleBody}
                        sx={{ color: '#014670', textTransform: 'none' }}
                      >
                        Generate from Parameters
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        bgcolor: '#1e1e1e',
                        overflow: 'hidden',
                      }}
                    >
                      <TextField
                        fullWidth
                        multiline
                        rows={14}
                        value={testRequestBody}
                        onChange={(e) => setTestRequestBody(e.target.value)}
                        placeholder='{"key": "value"}'
                        sx={{
                          height: '100%',
                          '& .MuiOutlinedInput-root': {
                            height: '100%',
                            alignItems: 'flex-start',
                            borderRadius: 0,
                            '& fieldset': { border: 'none' },
                          },
                          '& .MuiInputBase-input': {
                            fontFamily: '"Fira Code", "Consolas", monospace',
                            fontSize: '0.9rem',
                            lineHeight: 1.6,
                            bgcolor: '#1e1e1e',
                            color: '#d4d4d4',
                            p: 2,
                          },
                        }}
                      />
                    </Box>
                  </Card>
                </Grid>

                {/* Response */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                  <Card variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 350 }}>
                    <Box sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: testResponse && testResponse.length > 10000
                        ? 'error.50'
                        : testResponseWordCount !== null && testResponseWordCount > MAX_RESPONSE_WORDS
                          ? 'warning.50'
                          : testStatus?.ok ? 'success.50' : testStatus ? 'error.50' : 'grey.50',
                      flexShrink: 0,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Response
                        </Typography>
                        {testStatus && (
                          <Chip
                            icon={testStatus.ok ? <CheckCircle fontSize="small" /> : <ErrorIcon fontSize="small" />}
                            label={`${testStatus.code} ${testStatus.ok ? 'OK' : 'Error'}`}
                            size="small"
                            color={testStatus.ok ? 'success' : 'error'}
                          />
                        )}
                        {testResponseTime !== null && (
                          <Chip
                            icon={<Timer fontSize="small" />}
                            label={`${testResponseTime}ms`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {testResponse && (
                          <Chip
                            label={`${testResponse.length} chars`}
                            size="small"
                            color={testResponse.length > 10000 ? 'error' : 'default'}
                            variant={testResponse.length > 10000 ? 'filled' : 'outlined'}
                          />
                        )}
                        {testResponseWordCount !== null && (
                          <Chip
                            icon={<TextFields fontSize="small" />}
                            label={`${testResponseWordCount} words`}
                            size="small"
                            color={testResponseWordCount > MAX_RESPONSE_WORDS ? 'warning' : 'default'}
                            variant={testResponseWordCount > MAX_RESPONSE_WORDS ? 'filled' : 'outlined'}
                          />
                        )}
                      </Box>
                      {testResponse && (
                        <IconButton size="small" onClick={handleCopyResponse} title="Copy response">
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    {/* Error alert for character limit exceeded */}
                    {testResponse && testResponse.length > 10000 && (
                      <Alert
                        severity="error"
                        sx={{
                          borderRadius: 0,
                          '& .MuiAlert-message': { width: '100%' }
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          Response exceeds character limit ({testResponse.length.toLocaleString()} characters)
                        </Typography>
                        <Typography variant="caption">
                          This API returns more than 10,000 characters. You cannot save this tool until the response is reduced.
                          Please modify your API endpoint to return a smaller response.
                        </Typography>
                      </Alert>
                    )}

                    {/* Warning alert for large word count */}
                    {testResponseWordCount !== null && testResponseWordCount > MAX_RESPONSE_WORDS && (!testResponse || testResponse.length <= 10000) && (
                      <Alert
                        severity="warning"
                        icon={<Warning />}
                        sx={{
                          borderRadius: 0,
                          '& .MuiAlert-message': { width: '100%' }
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          Response too large ({testResponseWordCount.toLocaleString()} words)
                        </Typography>
                        <Typography variant="caption">
                          This API returns more than {MAX_RESPONSE_WORDS.toLocaleString()} words which will consume too many tokens.
                          Please modify your API to return a smaller response.
                        </Typography>
                      </Alert>
                    )}

                    <Box
                      sx={{
                        flex: 1,
                        overflow: 'auto',
                        bgcolor: '#1e1e1e',
                        p: 2,
                        fontFamily: '"Fira Code", "Consolas", monospace',
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        color: testStatus?.ok ? '#4ec9b0' : testStatus ? '#f48771' : '#d4d4d4',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        minHeight: 300,
                      }}
                    >
                      {testResponse || (
                        <Box sx={{ color: '#6a6a6a', fontStyle: 'italic', textAlign: 'center', pt: 8 }}>
                          <Science sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                          <Typography variant="body2">
                            Click "Send" to test the API endpoint
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {/* Show warning in footer if response is too large */}
          {toolDialogTab === 1 && testResponse && testResponse.length > 10000 && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="error" fontSize="small" />
              <Typography variant="caption" color="error.main" fontWeight={600}>
                Response exceeds 10,000 character limit - tool cannot be saved
              </Typography>
            </Box>
          )}
          {toolDialogTab === 1 && testResponseWordCount !== null && testResponseWordCount > MAX_RESPONSE_WORDS && (!testResponse || testResponse.length <= 10000) && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" fontSize="small" />
              <Typography variant="caption" color="warning.main">
                Response exceeds {MAX_RESPONSE_WORDS} word limit
              </Typography>
            </Box>
          )}
          <Button
            onClick={() => {
              setToolDialogOpen(false);
              setToolParameters([]);
              setTestRequestBody('{}');
              setTestResponse('');
              setTestStatus(null);
              setTestResponseTime(null);
              setTestResponseWordCount(null);
            }}
          >
            Cancel
          </Button>

          {/* Configuration tab: Show "Test API" button */}
          {toolDialogTab === 0 && (
            <Button
              variant="contained"
              onClick={() => setToolDialogTab(1)}
              disabled={!editingTool?.name || !editingTool?.description || !editingTool?.webhook_url}
              startIcon={<Science />}
              sx={{ bgcolor: '#014670', '&:hover': { bgcolor: '#014670' } }}
            >
              Test API
            </Button>
          )}

          {/* Test API tab: Show "Save/Update Tool" button */}
          {toolDialogTab === 1 && (
            <Button
              variant="contained"
              onClick={handleSaveTool}
              disabled={
                !editingTool?.name ||
                !editingTool?.description ||
                (testResponseWordCount !== null && testResponseWordCount > MAX_RESPONSE_WORDS)
              }
              startIcon={<Save />}
              sx={{ bgcolor: '#014670', '&:hover': { bgcolor: '#014670' } }}
            >
              {editingTool?.id ? 'Update Tool' : 'Create Tool'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Document Content Dialog */}
      <Dialog
        open={documentContentDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return; // block close
          }
          setDocumentContentDialogOpen(false);
        }}

        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '85vh' }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description sx={{ color: '#014670' }} />
              <Box>
                <Typography variant="h6">{selectedDocument?.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Extracted Text Content
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                bgcolor: 'rgba(124, 173, 24, 0.1)',
                color: '#014670',
                px: 1.5,
                py: 0.5,
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              Click below to edit
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'rgba(124, 173, 24, 0.02)' }}>
          <TextField
            fullWidth
            multiline
            autoFocus
            value={tempDocumentContent}
            onChange={(e) => setTempDocumentContent(e.target.value)}
            placeholder="No content extracted yet..."
            sx={{
              height: '100%',
              '& .MuiOutlinedInput-root': {
                height: '100%',
                alignItems: 'flex-start',
                borderRadius: 0,
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '0.95rem',
                lineHeight: 1.6,
                p: 3,
                '&::placeholder': {
                  color: 'rgba(0,0,0,0.4)',
                  opacity: 1,
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            {tempDocumentContent.length} characters • {tempDocumentContent.split(/\s+/).filter(w => w).length} words
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setDocumentContentDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveDocumentContent}
              sx={{ bgcolor: '#014670', '&:hover': { bgcolor: '#014670' } }}
            >
              Save Changes
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* System Prompt Dialog */}
      <Dialog
        open={systemPromptDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return; // block close
          }
          setSystemPromptDialogOpen(false);
        }}

        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Edit sx={{ color: '#014670' }} />
              <Typography variant="h6">Edit System Prompt</Typography>
            </Box>
            <Box
              sx={{
                bgcolor: 'rgba(124, 173, 24, 0.1)',
                color: '#014670',
                px: 1.5,
                py: 0.5,
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              Click below to type
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'rgba(124, 173, 24, 0.02)' }}>
          <TextField
            fullWidth
            multiline
            autoFocus
            value={tempSystemPrompt}
            onChange={(e) => setTempSystemPrompt(e.target.value)}
            placeholder="Enter instructions that define the chatbot's behavior and personality..."
            sx={{
              height: '100%',
              '& .MuiOutlinedInput-root': {
                height: '100%',
                alignItems: 'flex-start',
                borderRadius: 0,
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '1rem',
                lineHeight: 1.6,
                p: 3,
                '&::placeholder': {
                  color: 'rgba(0,0,0,0.4)',
                  opacity: 1,
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setSystemPromptDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleFieldChange('system_prompt', tempSystemPrompt);
              setSystemPromptDialogOpen(false);
            }}
            sx={{ bgcolor: '#014670', '&:hover': { bgcolor: '#014670' } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Avatar Dialog */}
      <Dialog
        open={avatarDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return; // block close
          }
          setAvatarDialogOpen(false);
        }}
        maxWidth={editingAvatar?.avatar_name ? "sm" : "md"}
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!editingAvatar?.avatar_name ? 'Select an Avatar' : editingAvatar?.id ? 'Edit Avatar Configuration' : 'Configure Avatar'}
            <Tooltip
              title="First select an avatar, then configure its languages."
              arrow
              placement="top"
            >
              <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Step 1: Avatar Selection Grid */}
            {!editingAvatar?.avatar_name ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  Choose an avatar for your chatbot from the options below
                </Typography>
                <Grid container spacing={2}>
                  {getAvailableAvatarsList().map((avatar) => (
                    <Grid size={{ xs: 6, sm: 4 }} key={avatar.id}>
                      <Paper
                        onClick={() => {
                          setEditingAvatar(prev => prev ? { ...prev, avatar_name: avatar.id } : null);
                        }}
                        elevation={0}
                        sx={{
                          border: '2px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#014670',
                            transform: 'scale(1.05)',
                            boxShadow: '0 4px 12px rgba(124, 173, 24, 0.25)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            paddingTop: '100%',
                            bgcolor: 'grey.100',
                          }}
                        >
                          <Box
                            component="img"
                            src={`${process.env.REACT_APP_API_BASE_URL}api/avatars/thumbnail/${avatar.id}`}
                            alt={avatar.name}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                          <Typography variant="body2" fontWeight={600} gutterBottom>
                            {avatar.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {avatar.description}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              // Step 2: Language Configuration (shown after avatar is selected)
              <Box>
                {/* Avatar Preview */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Box
                    component="img"
                    src={`${process.env.REACT_APP_API_BASE_URL}api/avatars/thumbnail/${editingAvatar.avatar_name}`}
                    alt={editingAvatar.avatar_name}
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '4px solid #014670',
                      boxShadow: '0 4px 12px rgba(124, 173, 24, 0.3)',
                    }}
                  />
                </Box>

                {/* Selected Avatar Name */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {getAvailableAvatarsList().find(a => a.id === editingAvatar.avatar_name)?.name}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setEditingAvatar(prev => prev ? { ...prev, avatar_name: '' } : null)}
                    sx={{ mt: 0.5, textTransform: 'none', color: 'text.secondary' }}
                  >
                    Change Avatar
                  </Button>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Languages *
                    </Typography>
                    <Tooltip
                      title="Select the languages this avatar will handle. When a user chats in one of these languages, this avatar will be used. You can select multiple languages."
                      arrow
                      placement="top"
                    >
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={LANGUAGE_OPTIONS}
                    getOptionLabel={(option) => {
                      // Handle both string (code) and object cases
                      if (typeof option === 'string') {
                        const found = LANGUAGE_OPTIONS.find(l => l.code === option);
                        return found ? found.label : option;
                      }
                      return option.label;
                    }}
                    value={
                      (editingAvatar?.languages || []).map(code => {
                        const found = LANGUAGE_OPTIONS.find(l => l.code === code);
                        return found || { code, name: code, label: code };
                      })
                    }
                    onChange={(_, newValue) => {
                      setEditingAvatar(prev => prev ? {
                        ...prev,
                        languages: newValue.map(item => typeof item === 'string' ? item : item.code)
                      } : null);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      const optCode = typeof option === 'string' ? option : option.code;
                      const valCode = typeof value === 'string' ? value : value.code;
                      return optCode === valCode;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type to search languages..."
                        helperText={`${editingAvatar?.languages?.length || 0} language(s) selected. Click checkboxes to select multiple.`}
                      />
                    )}
                    slotProps={{
                      chip: {
                        size: 'small',
                        sx: { bgcolor: '#014670', color: 'white', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }
                      }
                    }}
                    getOptionKey={(option) => typeof option === 'string' ? option : option.code}
                    ChipProps={{
                      size: 'small',
                      sx: { bgcolor: '#014670', color: 'white', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.code}>
                        <Checkbox
                          checked={selected}
                          sx={{
                            color: '#014670',
                            '&.Mui-checked': {
                              color: '#014670',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                          <Chip
                            label={option.code}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: 20,
                              borderColor: '#014670',
                              color: '#014670'
                            }}
                          />
                        </Box>
                      </li>
                    )}
                    sx={{ mb: 1 }}
                    filterOptions={(options, { inputValue }) => {
                      const filterValue = inputValue.toLowerCase();
                      return options.filter(
                        option =>
                          option.name.toLowerCase().includes(filterValue) ||
                          option.code.toLowerCase().includes(filterValue)
                      );
                    }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editingAvatar?.is_default || false}
                            onChange={(e) => setEditingAvatar(prev => prev ? { ...prev, is_default: e.target.checked } : null)}
                            sx={{
                              color: '#014670',
                              '&.Mui-checked': {
                                color: '#014670',
                              },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">Set as default avatar</Typography>
                            <Tooltip
                              title="The default avatar will be used when the user's language is not matched to any configured avatar, or as a fallback."
                              arrow
                              placement="top"
                            >
                              <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        }
                      />
                    </FormGroup>
                  </Box>
                  {editingAvatar?.is_default && (
                    <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                      This avatar will be used as the default for all unmatched languages.
                    </Alert>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
          <Button
            onClick={() => setAvatarDialogOpen(false)}
            variant="outlined"
            sx={{ borderColor: 'divider', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          {editingAvatar?.avatar_name && (
            <Button
              variant="contained"
              onClick={handleSaveAvatar}
              sx={{
                bgcolor: '#014670',
                '&:hover': { bgcolor: '#014670' },
              }}
            >
              {editingAvatar?.id ? 'Update Avatar' : 'Save Avatar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatbotEdit;
